"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TocElement = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const More_1 = require("@jsonjoy.com/ui/lib/icons/interactive/More");
const BasicButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton");
const Popup_1 = require("@jsonjoy.com/ui/lib/4-card/Popup");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const context_2 = require("../../../context");
const StripBarHandle_1 = require("../StripBarHandle");
const VoidSelectionOverlay_1 = require("../VoidSelectionOverlay");
const TocOptionsPopup_1 = require("./TocOptionsPopup");
const settings = tslib_1.__importStar(require("./settings"));
const blockClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '20px 0',
    bdrad: '6px',
    bxz: 'border-box',
    us: 'none',
});
const captionClass = (0, nano_theme_1.rule)({
    fz: '11px',
    fw: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    mb: '4px',
    mrl: '8px',
});
const listClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    gap: '0',
});
const itemClass = (0, nano_theme_1.rule)({
    d: 'block',
    pd: '1px 8px',
    bdrad: '4px',
    cur: 'pointer',
    trs: 'background 120ms ease, color 120ms ease',
    ws: 'nowrap',
    ov: 'hidden',
    textOverflow: 'ellipsis',
});
const itemTitleClass = (0, nano_theme_1.rule)({
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    textDecorationThickness: '1px',
    [`.${itemClass.trim()}:hover &`]: {
        textDecorationThickness: '2px',
    },
});
const stripWrapClass = (0, nano_theme_1.rule)({
    mrb: '2px',
    mrl: '8px',
});
const emptyClass = (0, nano_theme_1.rule)({
    fz: '13px',
    lh: 1.5,
});
const moreWrapClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    t: '-16px',
    insetInlineEnd: '8px',
    trs: 'opacity 0.15s ease',
    z: 1,
});
const computeNumbers = (items) => {
    const counters = [0, 0, 0, 0, 0, 0, 0];
    return items.map((item) => {
        const lvl = item.level;
        counters[lvl] = (counters[lvl] || 0) + 1;
        for (let i = lvl + 1; i < counters.length; i++)
            counters[i] = 0;
        if (lvl === 0)
            return { ...item };
        const parts = [];
        for (let i = 1; i <= lvl; i++)
            parts.push(counters[i] || 0);
        return { ...item, number: parts.join('.') + '.' };
    });
};
const TocElement = ({ attributes, children, element }) => {
    const styles = (0, context_1.useStyles)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const isSelected = (0, slate_react_1.useSelected)();
    const isFocused = (0, slate_react_1.useFocused)();
    const selected = isSelected && isFocused;
    const editor = (0, slate_react_1.useSlateStatic)();
    const mutxt = (0, context_2.useMuTxt)();
    const contentVersion = mutxt.contentVersion.use();
    const [hovered, setHovered] = React.useState(false);
    const maxLevel = settings.getTocMaxLevel(element.maxLevel);
    const includeTitle = settings.getTocIncludeTitle(element.includeTitle);
    const numbered = settings.getTocNumbered(element.numbered);
    const caption = element.caption?.trim() ?? '';
    // biome-ignore lint/correctness/useExhaustiveDependencies: contentVersion invalidates outline() cache
    const filtered = React.useMemo(() => {
        const all = mutxt.outline();
        return all.filter((item) => {
            if (item.level === 0)
                return includeTitle;
            return item.level <= maxLevel;
        });
    }, [mutxt, contentVersion, includeTitle, maxLevel]);
    const numberedItems = React.useMemo(() => (numbered ? computeNumbers(filtered) : filtered.map((i) => ({ ...i }))), [filtered, numbered]);
    const hasTitle = numberedItems[0]?.level === 0;
    const onItemClick = React.useCallback((item) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        mutxt.api.navigateTo(item.path);
    }, [mutxt]);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const selectBlock = React.useCallback(() => {
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, element);
            slate_1.Transforms.select(editor, path);
            slate_react_1.ReactEditor.focus(editor);
        }
        catch { }
    }, [editor, element]);
    const linkColor = styles.g(0.18);
    const captionColor = styles.g(0.42);
    const numberColor = styles.g(0.5);
    const hoverBg = styles.g(0, 0.05);
    const showToolbar = !readOnly && (selected || hovered);
    return (React.createElement("div", { ...attributes, className: blockClass, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
        React.createElement("div", { contentEditable: false },
            caption ? (React.createElement("div", { className: captionClass, style: { color: captionColor } }, caption)) : (React.createElement("div", { className: stripWrapClass },
                React.createElement(StripBarHandle_1.StripBarHandle, { ariaLabel: "Select table of contents", onActivate: () => selectBlock(), visible: hovered || selected, width: "11%", color: !styles.light ? styles.g(1, 0.32) : styles.g(0, 0.22), colorHover: !styles.light ? styles.g(1, 0.55) : styles.g(0, 0.45) }))),
            numberedItems.length ? (React.createElement("div", { className: listClass }, numberedItems.map((item) => {
                const indent = Math.max(0, item.level - (hasTitle ? 0 : 1)) * 32;
                const fontSize = item.level === 0 ? '1.05em' : item.level <= 2 ? '0.95em' : '0.9em';
                const fontWeight = item.level === 0 ? 600 : item.level === 1 ? 500 : 400;
                return (React.createElement("button", { key: item.key, type: "button", className: itemClass, onMouseDown: preventMouseDown, onClick: onItemClick(item), style: {
                        paddingInlineStart: indent + 8,
                        color: linkColor,
                        fontSize,
                        fontWeight,
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'start',
                        font: 'inherit',
                        width: '100%',
                    }, "data-toc-level": item.level, onMouseEnter: (e) => {
                        e.currentTarget.style.background = String(hoverBg);
                    }, onMouseLeave: (e) => {
                        e.currentTarget.style.background = 'transparent';
                    } },
                    item.number ? (React.createElement("span", { style: { color: numberColor, marginInlineEnd: 6, fontVariantNumeric: 'tabular-nums' } }, item.number)) : null,
                    React.createElement("span", { className: itemTitleClass }, item.title)));
            }))) : (React.createElement("div", { className: emptyClass, style: { color: captionColor } }, "Add headings to populate the table of contents."))),
        React.createElement(VoidSelectionOverlay_1.VoidSelectionOverlay, { selected: selected }),
        !readOnly && (React.createElement("div", { contentEditable: false, className: moreWrapClass, onMouseDown: preventMouseDown, style: { opacity: showToolbar ? 1 : 0, pointerEvents: showToolbar ? 'auto' : 'none' } },
            React.createElement(Popup_1.Popup, { renderContext: () => React.createElement(TocOptionsPopup_1.TocOptionsPopup, { element: element }) },
                React.createElement(BasicButton_1.BasicButton, { type: "button", width: 32, height: 32, rounder: true, border: true, onMouseDown: preventMouseDown },
                    React.createElement(More_1.More, { size: 32 }))))),
        children));
};
exports.TocElement = TocElement;
